// Rug Room Shots: one generation in full (GET /generations/{genId}): its
// snapshot, outcome, cost, review and the complete event trail (oldest first).
// Half-price attempts also show their batch (number, display name, Google name, line)
// and, on request, the batch's own batch_* events (GET /batches/{batchId}, SPEC 9.11).
// Read-only. Tolerant of extra/missing fields so backend additions do not break it.
// 2026-10-21: an attempt that sent the rug's detail photo as IMAGE 3 shows it (detailUrls, detailImageKey,
// detailSource; prompt v1d / v1d+note).
// AI check (SPEC_AICHECK 4.6 / 5.10): the "AI check" section (AiCheckPanel, with Check again) above the field table and
// "Earlier AI checks" (aiChecks, newest first). Check again queues a check of this exact image (a few cents; also an
// image that was regenerated since) and re-reads this generation every 8 s while the check waits or runs.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Alert, CircularProgress,
  Table, TableBody, TableRow, TableCell, Stack, Collapse, IconButton, Chip, Paper, Divider,
} from '@mui/material';
import { MdExpandMore, MdExpandLess, MdClose } from 'react-icons/md';
import rrsApi from './api';
import {
  GEN_STATUS, REVIEW_STATE, StatusChip, Tile, fmtNyFull, fmtUsd, errorWords, errText, REJECT_REASON_LABELS, parseJsonish,
  AI_STATE, fmtNy, usePolling,
} from './common';
import { SpeedChip, BatchStateChip, speedLabel, isBatch, batchErrorText } from './speed';
import { DETAIL_SOURCE_WORDS } from './DetailPhotoBlock';
import AiCheckPanel, { requestAiCheck } from './AiCheckPanel';

const CHECK_STATUS_WORDS = {
  queued: 'Waiting', running: 'Checking', cancelled: 'Stopped before it ran',
};

// Earlier AI checks of this image (newest first, at most 20): when, how it was asked, the result or the error.
// Review fix RP-7: one block per check that stacks on a phone (a 4-column table ran off a 390 px screen) and is a row
// from 600 px up.
const AiCheckHistory = ({ checks }) => {
  const [open, setOpen] = useState(false);
  if (!checks || !checks.length) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Button size="small" onClick={() => setOpen((o) => !o)} endIcon={open ? <MdExpandLess /> : <MdExpandMore />} sx={{ px: 0.5 }}>
        Earlier AI checks ({checks.length})
      </Button>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={0.75} divider={<Divider flexItem />} sx={{ mt: 0.5 }} data-testid="ai-check-history">
          {checks.map((c) => {
            const verdict = c.status === 'done' && c.verdict ? AI_STATE[c.verdict] : null;
            const when = c.finishedAt || c.requestedAt;
            const who = c.origin === 'auto' ? 'automatic' : (c.requestedByEmail || 'asked by a person');
            const meta = [c.model, c.promptVersion].filter(Boolean).join(' · ')
              + (c.costUsd !== null && c.costUsd !== undefined ? ` · ${fmtUsd(c.costUsd, 3)}` : '');
            return (
              <Stack
                key={c.checkId} direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 1.5 }}
                alignItems={{ sm: 'flex-start' }} sx={{ minWidth: 0 }}
              >
                <Box sx={{ width: { sm: 120 }, flexShrink: 0 }}>
                  <Typography variant="body2" title={when ? `${fmtNyFull(when)} (New York)` : ''}>{when ? fmtNy(when) : '-'}</Typography>
                </Box>
                <Box sx={{ width: { sm: 160 }, flexShrink: 0, minWidth: 0 }}>
                  <Typography variant="body2" noWrap title={who}>{who}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {verdict ? (
                    <Chip
                      size="small" color={verdict.color} variant="filled" sx={{ fontWeight: 700 }}
                      label={`AI${c.score !== null && c.score !== undefined ? ` ${c.score}` : ''} · ${verdict.label}`}
                    />
                  ) : null}
                  {c.status === 'failed' ? (
                    <Typography variant="body2" color="warning.main">Could not check: {c.errorText || c.errorCode || 'the AI check failed.'}</Typography>
                  ) : null}
                  {CHECK_STATUS_WORDS[c.status] ? <Typography variant="body2" color="text.secondary">{CHECK_STATUS_WORDS[c.status]}</Typography> : null}
                  {c.status === 'done' && c.why ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>{c.why}</Typography>
                  ) : null}
                </Box>
                {meta ? (
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: { sm: 'nowrap' } }}>
                    {meta}
                  </Typography>
                ) : null}
              </Stack>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
};

const pick = (obj, ...paths) => {
  for (let i = 0; i < paths.length; i += 1) {
    const v = paths[i].split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return null;
};

const FIELDS = [
  ['Attempt', (g) => g.attempt],
  ['Job', (g) => (g.jobId ? `#${g.jobId}` : null)],
  ['WebID / ItemID', (g) => [g.webId, g.itemId].filter(Boolean).join(' / ')],
  ['Shape', (g) => g.shapeCode],
  ['Style', (g) => g.styleCode],
  ['Room', (g) => g.roomCode],
  ['Base image', (g) => g.baseImageKey],
  ['Silo image', (g) => g.siloImageKey],
  ['Detail photo (image 3)', (g) => (g.detailImageKey
    ? `${g.detailImageKey}${g.detailSource ? ` · ${DETAIL_SOURCE_WORDS[g.detailSource] || g.detailSource}` : ''}` : null)],
  ['Model', (g) => [g.model, g.imageSize, g.aspectRatio].filter(Boolean).join(' · ')],
  ['Speed', (g) => (g.speed ? `${speedLabel(g.speed)}${isBatch(g.speed) ? ' (Gemini Batch API, half price)' : ' (real time)'}` : null)],
  ['Batch', (g) => {
    const b = g.batch;
    if (!b || !b.batchId) return g.batchId ? `#${g.batchId}${g.batchLineNo ? ` · line ${g.batchLineNo}` : ''}` : null;
    return `#${b.batchId}${b.lineNo ? ` · line ${b.lineNo}${b.lineCount ? ` of ${b.lineCount}` : ''}` : ''}${b.batchKey ? ` · key ${b.batchKey}` : ''}`;
  }],
  ['Batch name', (g) => (g.batch && g.batch.displayName) || null],
  ['Google batch', (g) => (g.batch && g.batch.googleName) || null],
  ['Batch sent', (g) => (g.batch && g.batch.submittedAt ? fmtNyFull(g.batch.submittedAt) : null)],
  ['Batch last checked', (g) => (g.batch && g.batch.lastPolledAt ? fmtNyFull(g.batch.lastPolledAt) : null)],
  ['Google finished', (g) => (g.batch && g.batch.terminalAt ? fmtNyFull(g.batch.terminalAt) : null)],
  ['Model version', (g) => g.modelVersion],
  ['Prompt', (g) => g.promptVersion],
  ['Note', (g) => (g.note ? `${g.note}${g.noteInPrompt ? ' (added to the prompt)' : ''}` : null)],
  ['Requested', (g) => (g.requestedAt ? `${fmtNyFull(g.requestedAt)} by ${g.requestedByEmail || '?'}` : null)],
  ['Sent to Google', (g) => (g.sentAt ? fmtNyFull(g.sentAt) : null)],
  ['Finished', (g) => (g.finishedAt ? fmtNyFull(g.finishedAt) : null)],
  ['Tries', (g) => g.tries],
  ['HTTP / finish', (g) => [g.httpStatus, g.finishReason].filter((x) => x !== null && x !== undefined).join(' · ')],
  ['Latency', (g) => (g.latencyMs ? `${(g.latencyMs / 1000).toFixed(1)} s` : null)],
  ['Output', (g) => (g.outW ? `${g.outW} x ${g.outH}${g.outputBytes ? ` · ${(g.outputBytes / 1048576).toFixed(1)} MB` : ''}` : null)],
  ['Cost', (g) => (g.costUsd !== null && g.costUsd !== undefined ? fmtUsd(g.costUsd, 4) : null)],
  ['Error', (g) => {
    if (!g.errorCode && !g.errorMessage) return null;
    if (String(g.errorCode || '').startsWith('batch_')) return errorWords(g.errorCode, g.errorMessage);
    return `${g.errorText || errorWords(g.errorCode, g.errorMessage)}${g.errorText && g.errorMessage ? ` (${g.errorMessage})` : ''}`;
  }],
  ['Usage', (g) => (g.usage ? Object.entries(g.usage).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => `${k} ${v}`).join(' · ') : null)],
  ['Reviewed', (g) => (g.reviewedAt ? `${fmtNyFull(g.reviewedAt)} by ${g.reviewedByEmail || '?'}` : null)],
  ['Reject reason', (g) => (g.reviewReasonCode ? (REJECT_REASON_LABELS[g.reviewReasonCode] || g.reviewReasonCode) : null)],
  ['Review note', (g) => g.reviewNote],
  ['Superseded by', (g) => (g.supersededByGenId ? `gen ${g.supersededByGenId}` : null)],
  ['S3 original', (g) => g.s3OriginalKey],
  ['Request sha256', (g) => g.requestSha256],
  ['Output sha256', (g) => g.outputSha256],
  ['Response id', (g) => g.responseId],
];

const EventRow = ({ ev: raw }) => {
  const [open, setOpen] = useState(false);
  const ev = { ...raw, payload: parseJsonish(raw.payload) };
  return (
    <>
      <TableRow hover>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtNyFull(ev.ts)}</TableCell>
        <TableCell sx={{ fontFamily: 'monospace' }}>{ev.eventType}</TableCell>
        <TableCell>{ev.actorEmail || ev.actorWorkerId || ev.actorType}</TableCell>
        <TableCell padding="checkbox">
          {ev.payload ? (
            <IconButton size="small" onClick={() => setOpen((o) => !o)}>{open ? <MdExpandLess /> : <MdExpandMore />}</IconButton>
          ) : null}
        </TableCell>
      </TableRow>
      {ev.payload ? (
        <TableRow>
          <TableCell colSpan={4} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
            <Collapse in={open} unmountOnExit>
              <Box component="pre" sx={{ m: 0, my: 1, p: 1, fontSize: 12, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto' }}>
                {JSON.stringify(ev.payload, null, 2)}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};

// The batch this attempt belonged to: state, counts and its batch_* events (on request).
const BatchEvents = ({ batchId }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await rrsApi.getBatch(batchId, 500));
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => { if (open && !data && !loading && !error) load(); }, [open, data, loading, error, load]);

  const b = data && data.batch;
  const events = (data && data.events) || [];
  return (
    <Box sx={{ mt: 2 }}>
      <Button size="small" onClick={() => setOpen((o) => !o)} endIcon={open ? <MdExpandLess /> : <MdExpandMore />}>
        {open ? 'Hide' : 'Show'} batch #{batchId} events
      </Button>
      <Collapse in={open} unmountOnExit>
        {loading && <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        {b && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
            <BatchStateChip batch={b} />
            <Typography variant="body2">
              {b.lineCount} image(s) · {b.linesCollected || 0} back
              {b.submittedAt ? ` · sent ${fmtNyFull(b.submittedAt)}` : ''}
              {b.pollCount ? ` · checked ${b.pollCount} time(s)` : ''}
            </Typography>
            {batchErrorText(b) ? <Typography variant="body2" color="error.main">{batchErrorText(b)}</Typography> : null}
          </Stack>
        )}
        {data && (
          events.length === 0 ? <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No batch events yet.</Typography> : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableBody>{events.map((ev) => <EventRow key={ev.eventId} ev={ev} />)}</TableBody>
            </Table>
          )
        )}
        {data && data.eventsTruncated && (
          <Typography variant="caption" color="text.secondary">Showing the first {events.length} events; the full list is in the Spend &amp; log tab.</Typography>
        )}
      </Collapse>
    </Box>
  );
};

// aiEnabled: config.features.aiCheck of the page (undefined = not known here: the buttons show and the backend
// refuses with worker_not_ready if the worker is not ready). onAiChanged: the parent list re-reads (chips, polling).
// onUseNote(gen, note): only where the parent can open its Regenerate form for this image.
const GenerationDetailDialog = ({
  genId, onClose, aiEnabled, onAiChanged, onUseNote,
}) => {
  const [gen, setGen] = useState(null);
  const [error, setError] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMsg, setAiMsg] = useState(null); // { severity, text }

  const load = useCallback(async () => {
    if (!genId) return;
    setGen(null);
    setError('');
    setAiMsg(null);
    try {
      setGen(await rrsApi.getGeneration(genId));
    } catch (e) {
      setError(errText(e));
    }
  }, [genId]);

  // A quiet re-read (no spinner) while an AI check of this image waits or runs.
  const reload = useCallback(async () => {
    if (!genId) return;
    try {
      const g = await rrsApi.getGeneration(genId);
      if (g && Number(g.genId) === Number(genId)) setGen(g);
    } catch (e) { /* the next poll tries again */ }
  }, [genId]);

  const checkAgain = useCallback(async () => {
    if (!genId || aiBusy) return;
    setAiBusy(true);
    setAiMsg(null);
    const r = await requestAiCheck(genId, gen && gen.itemId);
    setAiBusy(false);
    setAiMsg({ severity: r.severity, text: r.message });
    if (r.ok || r.already) {
      await reload();
      if (onAiChanged) onAiChanged();
    }
  }, [genId, aiBusy, gen, reload, onAiChanged]);

  useEffect(() => { load(); }, [load]);
  const aiWaiting = !!(gen && gen.aiCheck && gen.aiCheck.checking);
  usePolling(reload, 8000, !!genId && aiWaiting);
  const batchId = gen ? ((gen.batch && gen.batch.batchId) || gen.batchId || null) : null;

  const preview = gen && pick(gen, 'result.previewUrl', 'previewUrl', 'urls.previewUrl', 'urls.preview', 'result.thumbUrl', 'thumbUrl');
  const original = gen && pick(gen, 'result.originalUrl', 'originalUrl', 'urls.originalUrl', 'urls.original');
  const silo = gen && pick(gen, 'siloUrls.smallUrl', 'silo.smallUrl', 'siloUrls.originalUrl', 'siloUrl');
  const base = gen && pick(gen, 'baseUrls.smallUrl', 'base.smallUrl', 'baseUrls.originalUrl', 'baseUrl');
  const detail = gen && pick(gen, 'detailUrls.smallUrl', 'detailUrls.largeUrl', 'detailUrls.originalUrl');
  const tiles = [['Silo', silo], ['Base room', base], ['Result', preview]];
  if (detail) tiles.splice(2, 0, ['Detail photo', detail]);
  const events = (gen && (gen.events || [])) || [];

  return (
    <Dialog open={!!genId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Generation {genId}
        {gen && (
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            <StatusChip map={GEN_STATUS} status={gen.status} />
            {gen.status === 'succeeded' && <StatusChip map={REVIEW_STATE} status={gen.reviewState} />}
            {gen.speed ? <SpeedChip speed={gen.speed} /> : null}
          </Stack>
        )}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close"><MdClose /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {!gen && !error && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>}
        {gen && (
          <>
            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
              {tiles.map(([label, src]) => (
                <Box key={label} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Tile src={src} alt={label} size={200} sx={{ width: '100%', height: 'auto', aspectRatio: '1 / 1' }} />
                </Box>
              ))}
            </Stack>
            {original && (
              <Button size="small" href={original} target="_blank" rel="noopener noreferrer" sx={{ mb: 1 }}>Download 4K original</Button>
            )}
            {gen.aiCheck ? (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>AI check</Typography>
                {aiMsg && <Alert severity={aiMsg.severity} sx={{ mb: 1 }} onClose={() => setAiMsg(null)}>{aiMsg.text}</Alert>}
                <AiCheckPanel
                  ai={gen.aiCheck}
                  item={gen}
                  enabled={aiEnabled !== false}
                  busy={aiBusy}
                  onCheckAgain={checkAgain}
                  onUseNote={onUseNote ? (note) => onUseNote(gen, note) : undefined}
                />
                <AiCheckHistory checks={Array.isArray(gen.aiChecks) ? gen.aiChecks : []} />
              </Paper>
            ) : null}
            <Table size="small">
              <TableBody>
                {FIELDS.map(([label, fn]) => {
                  const v = fn(gen);
                  if (v === null || v === undefined || v === '') return null;
                  return (
                    <TableRow key={label}>
                      <TableCell sx={{ width: 170, color: 'text.secondary' }}>{label}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-all' }}>{String(v)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {batchId ? <BatchEvents key={batchId} batchId={batchId} /> : null}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Event trail ({events.length})</Typography>
            {events.length === 0 ? <Typography variant="body2" color="text.secondary">No events.</Typography> : (
              <Table size="small">
                <TableBody>{events.map((ev) => <EventRow key={ev.eventId} ev={ev} />)}</TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

export default GenerationDetailDialog;
export { EventRow };
